import React, { useState, useRef, useEffect } from 'react';
import { 
  Search, 
  CreditCard, 
  DollarSign, 
  Printer,
  FileText,
  Check,
  ChevronDown
} from 'lucide-react';

const ExaminationPayment = ({ onBack }) => {
  // Sample examination data with detailed test information
  const [examData, setExamData] = useState([
    { 
      id: 1, 
      voucherNo: 'V001', 
      patientName: 'Rajesh Kumar', 
      patientCode: 'PT001', 
      totalAmount: 650, 
      tests: [
        { name: 'Complete Blood Count (CBC)', price: 400 },
        { name: 'Blood Glucose Fasting', price: 250 }
      ] 
    },
    { 
      id: 2, 
      voucherNo: 'V002', 
      patientName: 'Priya Sharma', 
      patientCode: 'PT002', 
      totalAmount: 850, 
      tests: [
        { name: 'Thyroid Profile', price: 850 }
      ] 
    },
    { 
      id: 3, 
      voucherNo: 'V003', 
      patientName: 'Amit Verma', 
      patientCode: 'PT003', 
      totalAmount: 1500, 
      tests: [
        { name: 'Lipid Profile', price: 650 },
        { name: 'Liver Function Test', price: 750 }
      ] 
    },
    { 
      id: 4, 
      voucherNo: 'V004', 
      patientName: 'Sneha Patel', 
      patientCode: 'PT004', 
      totalAmount: 350, 
      tests: [
        { name: 'ECG', price: 350 }
      ] 
    },
    { 
      id: 5, 
      voucherNo: 'V005', 
      patientName: 'Vikram Singh', 
      patientCode: 'PT005', 
      totalAmount: 500, 
      tests: [
        { name: 'X-Ray Chest', price: 500 }
      ] 
    }
  ]);

  const [formData, setFormData] = useState({
    patientSearch: '',
    voucherSearch: '',
    selectedExam: null,
    paymentMethod: 'cash',
    paidAmount: 0,
    paymentDate: new Date().toISOString().split('T')[0]
  });

  const [showPatientDropdown, setShowPatientDropdown] = useState(false);
  const [filteredExams, setFilteredExams] = useState([]);
  const [showReceipt, setShowReceipt] = useState(false);
  const printRef = useRef();
  const patientSearchRef = useRef();

  // Filter examinations based on search terms
  useEffect(() => {
    let filtered = examData;
    
    // Filter by patient name if search term exists
    if (formData.patientSearch) {
      filtered = filtered.filter(exam => 
        exam.patientName.toLowerCase().includes(formData.patientSearch.toLowerCase()) ||
        exam.patientCode.toLowerCase().includes(formData.patientSearch.toLowerCase())
      );
    }
    
    // Filter by voucher number if search term exists
    if (formData.voucherSearch) {
      filtered = filtered.filter(exam => 
        exam.voucherNo.toLowerCase().includes(formData.voucherSearch.toLowerCase())
      );
    }
    
    setFilteredExams(filtered);
  }, [formData.patientSearch, formData.voucherSearch, examData]);

  // Update paid amount when exam is selected
  useEffect(() => {
    if (formData.selectedExam) {
      setFormData(prev => ({
        ...prev,
        paidAmount: prev.selectedExam.totalAmount
      }));
    }
  }, [formData.selectedExam]);

  // Handle patient search input change
  const handlePatientSearchChange = (value) => {
    setFormData(prev => ({
      ...prev,
      patientSearch: value,
      selectedExam: null
    }));
    
    // If patient search is cleared, clear voucher search too
    if (!value) {
      setFormData(prev => ({
        ...prev,
        voucherSearch: ''
      }));
    }
    
    setShowPatientDropdown(true);
  };

  // Handle voucher search input change
  const handleVoucherSearchChange = (value) => {
    setFormData(prev => ({
      ...prev,
      voucherSearch: value,
      selectedExam: null
    }));
    
    // If voucher search is entered, find the exam and update patient search
    if (value) {
      const exam = examData.find(e => e.voucherNo === value);
      if (exam) {
        setFormData(prev => ({
          ...prev,
          patientSearch: `${exam.patientCode} - ${exam.patientName}`,
          selectedExam: exam
        }));
        setFilteredExams([exam]);
        setShowPatientDropdown(false);
      }
    } else {
      // If voucher search is cleared, clear patient search too
      setFormData(prev => ({
        ...prev,
        patientSearch: ''
      }));
      setFilteredExams(examData);
    }
  };

  const handleSelectExam = (exam) => {
    setFormData(prev => ({
      ...prev,
      selectedExam: exam,
      patientSearch: `${exam.patientCode} - ${exam.patientName}`,
      voucherSearch: exam.voucherNo
    }));
    setShowPatientDropdown(false);
  };

  const handlePaymentSubmit = (e) => {
    e.preventDefault();
    if (!formData.selectedExam) {
      alert('Please select an examination first');
      return;
    }
    setShowReceipt(true);
  };

  const handlePrint = () => {
    const printContent = printRef.current.innerHTML;
    const originalContent = document.body.innerHTML;
    
    document.body.innerHTML = printContent;
    window.print();
    document.body.innerHTML = originalContent;
  };

  const handleNewPayment = () => {
    setFormData({
      patientSearch: '',
      voucherSearch: '',
      selectedExam: null,
      paymentMethod: 'cash',
      paidAmount: 0,
      paymentDate: new Date().toISOString().split('T')[0]
    });
    setShowReceipt(false);
    setFilteredExams(examData);
    setShowPatientDropdown(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (patientSearchRef.current && !patientSearchRef.current.contains(event.target)) {
        setShowPatientDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="bg-white rounded-lg shadow-md">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 rounded-t-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
              </svg>
            </button>
            <div>
              <h1 className="text-2xl font-bold">Examination Payment</h1>
              <p className="text-blue-100">Process payments for examinations</p>
            </div>
          </div>
          <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
            <CreditCard className="w-6 h-6" />
          </div>
        </div>
      </div>

      {!showReceipt ? (
        /* Payment Form */
        <form onSubmit={handlePaymentSubmit} className="p-6 space-y-6">
          {/* Patient and Voucher Search */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="relative" ref={patientSearchRef}>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search Patient
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  value={formData.patientSearch}
                  onChange={(e) => handlePatientSearchChange(e.target.value)}
                  onFocus={() => setShowPatientDropdown(true)}
                  placeholder="Search by patient name or code..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <ChevronDown 
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 cursor-pointer"
                  onClick={() => setShowPatientDropdown(!showPatientDropdown)}
                />
              </div>

              {/* Patient Dropdown */}
              {showPatientDropdown && filteredExams.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {filteredExams.map(exam => (
                    <div
                      key={exam.id}
                      onClick={() => handleSelectExam(exam)}
                      className="p-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                    >
                      <div className="font-medium">{exam.patientName}</div>
                      <div className="text-sm text-gray-600">
                        {exam.patientCode} • Voucher: {exam.voucherNo} • Amount: ₹{exam.totalAmount}
                      </div>
                      <div className="text-xs text-gray-500">
                        Tests: {exam.tests.map(t => t.name).join(', ')}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search Voucher No. <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  value={formData.voucherSearch}
                  onChange={(e) => handleVoucherSearchChange(e.target.value)}
                  placeholder="Search by voucher number..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
            </div>
          </div>

          {/* Selected Examination Details */}
          {formData.selectedExam && (
            <div className="border border-green-200 rounded-lg p-4 bg-green-50">
              <h3 className="font-semibold text-green-800 mb-3 flex items-center">
                <Check className="w-5 h-5 mr-2" />
                Selected Examination
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-gray-600">Patient</div>
                  <div className="font-medium">{formData.selectedExam.patientName}</div>
                  <div className="text-sm text-gray-600">{formData.selectedExam.patientCode}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Voucher No.</div>
                  <div className="font-medium">{formData.selectedExam.voucherNo}</div>
                </div>
                <div className="md:col-span-2">
                  <div className="text-sm text-gray-600">Tests</div>
                  <div className="space-y-2 mt-1">
                    {formData.selectedExam.tests.map((test, index) => (
                      <div key={index} className="flex justify-between items-center bg-white p-2 rounded border">
                        <div className="font-medium">{test.name}</div>
                        <div className="text-green-700 font-semibold">₹{test.price}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="md:col-span-2 border-t pt-3">
                  <div className="flex justify-between items-center">
                    <div className="text-sm text-gray-600">Total Amount</div>
                    <div className="text-xl font-bold text-green-700">₹{formData.selectedExam.totalAmount}</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Payment Details */}
          {formData.selectedExam && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">Payment Details</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Method <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <label className={`flex flex-col items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                      formData.paymentMethod === 'cash' 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-300 hover:border-gray-400'
                    }`}>
                      <DollarSign className="w-6 h-6 mb-1" />
                      <span>Cash</span>
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="cash"
                        checked={formData.paymentMethod === 'cash'}
                        onChange={(e) => setFormData({...formData, paymentMethod: e.target.value})}
                        className="sr-only"
                      />
                    </label>
                    <label className={`flex flex-col items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                      formData.paymentMethod === 'digital' 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-300 hover:border-gray-400'
                    }`}>
                      <CreditCard className="w-6 h-6 mb-1" />
                      <span>Digital</span>
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="digital"
                        checked={formData.paymentMethod === 'digital'}
                        onChange={(e) => setFormData({...formData, paymentMethod: e.target.value})}
                        className="sr-only"
                      />
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.paymentDate}
                    onChange={(e) => setFormData({...formData, paymentDate: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Amount Paid (₹) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={formData.paidAmount}
                  onChange={(e) => setFormData({...formData, paidAmount: parseFloat(e.target.value) || 0})}
                  min="0"
                  max={formData.selectedExam.totalAmount}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
                <div className="text-sm text-gray-600 mt-1">
                  Total amount: ₹{formData.selectedExam.totalAmount}
                  {formData.paidAmount < formData.selectedExam.totalAmount && (
                    <span className="text-orange-600 ml-2">
                      (Balance: ₹{formData.selectedExam.totalAmount - formData.paidAmount})
                    </span>
                  )}
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex justify-end pt-4">
                <button
                  type="submit"
                  className="bg-gradient-to-r from-green-600 to-green-700 text-white px-6 py-3 rounded-lg font-medium hover:from-green-700 hover:to-green-800 transition-all duration-200 flex items-center space-x-2 shadow-lg"
                >
                  <CreditCard className="w-5 h-5" />
                  <span>Process Payment</span>
                </button>
              </div>
            </div>
          )}
        </form>
      ) : (
        /* Receipt Display */
        <div className="p-6">
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-green-800 flex items-center">
                  <Check className="w-6 h-6 mr-2" />
                  Payment Successful!
                </h2>
                <p className="text-green-600">Payment has been processed successfully.</p>
              </div>
              <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                Paid
              </div>
            </div>
          </div>

          {/* Receipt Content */}
          <div ref={printRef} className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
            <div className="text-center mb-6">
              <h1 className="text-2xl font-bold">Bluewell Diagnostic Centre</h1>
              <p className="text-gray-600">Payment Receipt</p>
              <p className="text-gray-500 text-sm">Date: {new Date(formData.paymentDate).toLocaleDateString()}</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <div className="text-sm text-gray-600">Patient Name</div>
                <div className="font-medium">{formData.selectedExam.patientName}</div>
                <div className="text-sm text-gray-600">{formData.selectedExam.patientCode}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600">Voucher No.</div>
                <div className="font-medium">{formData.selectedExam.voucherNo}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600">Payment Method</div>
                <div className="font-medium capitalize">{formData.paymentMethod}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600">Payment Date</div>
                <div className="font-medium">{new Date(formData.paymentDate).toLocaleDateString()}</div>
              </div>
            </div>
            
            {/* Test Details Table */}
            <div className="mb-6">
              <h3 className="font-semibold text-gray-800 mb-3">Test Details</h3>
              <table className="w-full border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 p-2 text-left">Test Name</th>
                    <th className="border border-gray-300 p-2 text-right">Price (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.selectedExam.tests.map((test, index) => (
                    <tr key={index}>
                      <td className="border border-gray-300 p-2">{test.name}</td>
                      <td className="border border-gray-300 p-2 text-right">₹{test.price}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-300 p-2 font-bold">Total Amount</td>
                    <td className="border border-gray-300 p-2 text-right font-bold">₹{formData.selectedExam.totalAmount}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            
            <div className="border-t border-b border-gray-200 py-4 mb-4">
              <div className="flex justify-between items-center">
                <div className="font-semibold">Total Amount</div>
                <div className="text-lg font-bold">₹{formData.selectedExam.totalAmount}</div>
              </div>
              <div className="flex justify-between items-center mt-2">
                <div className="font-semibold">Amount Paid</div>
                <div className="text-lg font-bold text-green-700">₹{formData.paidAmount}</div>
              </div>
              {formData.paidAmount < formData.selectedExam.totalAmount && (
                <div className="flex justify-between items-center mt-2">
                  <div className="font-semibold">Balance Amount</div>
                  <div className="text-lg font-bold text-orange-600">₹{formData.selectedExam.totalAmount - formData.paidAmount}</div>
                </div>
              )}
            </div>
            
            <div className="text-center text-sm text-gray-600 mt-6">
              Thank you for choosing Bluewell Diagnostic Centre
            </div>
            <div className="text-center text-xs text-gray-500 mt-2">
              Receipt generated on: {new Date().toLocaleString()}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3">
            <button
              onClick={handleNewPayment}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-center"
            >
              <FileText className="w-4 h-4 mr-2" />
              New Payment
            </button>
            <button
              onClick={handlePrint}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print Receipt
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExaminationPayment;